/**
 * App Configuration Hook
 * Fetches backend-controlled feature flags (no auth required)
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import type { ApiResponse } from '../types';

interface AppConfig {
  /** 驗收單路徑：憑單過帳後業務端開立電子發票的總開關 */
  enable_frontend_einvoice: boolean;
  /** 訂金路徑：收款時自動開立電子發票的開關（2026-08-28 從 enable_frontend_einvoice 拆出） */
  enable_deposit_einvoice: boolean;
}

const DEFAULT_CONFIG: AppConfig = {
  enable_frontend_einvoice: false,
  enable_deposit_einvoice: false,
};

export function useAppConfig() {
  // 這兩個旗標自 2026-08-28 起逐公司（山恩開、玉禮關），後端依 `X-Company-Id`
  // 回答。companyId 必須進 queryKey，否則切換公司會沿用前一家公司的快取
  // （staleTime 1 小時），業務會在玉禮看到山恩的開票按鈕。
  const companyId = useAuthStore((s) => s.currentCompanyId);

  return useQuery({
    queryKey: ['app-config', companyId],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<AppConfig>>('/api/saiens/app-config');
      return res.data.data ?? DEFAULT_CONFIG;
    },
    // 還沒解析出公司就先不打：後端 fail-closed 會回 false，那個 false 會被
    // 快取一小時，變成「登入後按鈕永遠不出現」。
    enabled: !!companyId,
    staleTime: 60 * 60 * 1000, // 1 hour cache
    gcTime: 60 * 60 * 1000,
  });
}
